import { getEntryRecordResultApi } from 'api/client';
import { AI_FLOW_KEY, ENTRY_RESULT_RECORD_KEY } from 'client/config/entryConfig';
import {
    AI_SETTING_KEY,
    AI_SETTING_MAP,
    EXTRA_UPLOAD_FEATURE_MAP,
    isEnableAISettingKey
} from 'config/aiConfig';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';
import * as clientErrTypes from 'config/clientErrorTypes';
import { gameTypes } from 'config/promoGameTypes';
import { EXTRA_UPLOAD_FEATURE_TYPES } from 'config/gameSettingConfig';

const CAN_GET_ENTRY_RECORD_RESULT_PROMO_TYPE = new Set([gameTypes.PSYCHO_QUIZ]);

export const hasGetEntryRecordResultPromoType = promoType =>
    CAN_GET_ENTRY_RECORD_RESULT_PROMO_TYPE.has(promoType);

export default function useGetEntryRecordResult() {
    const errHandler = useClientErrorHandler();
    const { enableExtraUploadFeature, enableTransitionPage } = useSelector(s => s.settingPromo);
    const { extraUploadFeatureTypes = [] } = useSelector(s => s.settingGame);
    const { uuid: promoUUID } = useSelector(s => s.promo);
    const { currentEntryUUID } = useSelector(s => s.userEntryState);
    const aiExtensions = useSelector(s => s.settingAIExtensions);
    const recordKey = Object.values(ENTRY_RESULT_RECORD_KEY);

    const getEntryRecordResult = useCallback(async () => {
        const result = {
            ...recordKey.reduce((acc, key) => {
                acc[key] = null;
                return acc;
            }, {}),
            isAIFlowFinished: true,
            isUploadFlowFinished: true,
            enableTransitionPage
        };
        let imageUrl = null;
        const enableAiImageExtensions = aiExtensions?.enableResultImage;

        if (enableExtraUploadFeature || isEnableAISettingKey(aiExtensions)) {
            try {
                const response = await getEntryRecordResultApi({
                    promoUUID,
                    entryUUID: currentEntryUUID
                });
                const lastResult = response[0];

                // 防護檢查：如果沒有結果資料，直接返回預設值
                if (!lastResult || !lastResult.records) {
                    return {
                        data: response,
                        ...result,
                        imageUrl
                    };
                }

                const lastResultRecordData = lastResult.records.reduce(
                    (acc, { key, value }) => ({
                        ...acc,
                        [key]: value
                    }),
                    {}
                );

                recordKey.forEach(key => (result[key] = lastResultRecordData[key] ?? null));

                if (enableExtraUploadFeature) {
                    extraUploadFeatureTypes.forEach(type => {
                        const recordKey = EXTRA_UPLOAD_FEATURE_MAP[type];
                        if (recordKey && result[recordKey] === null) {
                            result[recordKey] = '';
                        }
                    });
                }

                if (isEnableAISettingKey(aiExtensions)) {
                    Object.entries(AI_SETTING_MAP).forEach(([settingKey, recordKey]) => {
                        if (aiExtensions[settingKey] && result[recordKey] === null) {
                            result[recordKey] = '';
                        }
                        // 如果開啟圖片上傳功能但沒有開 AI，則將圖片上傳設為空字串
                        if (
                            aiExtensions[AI_SETTING_KEY.ENABLE_RESULT_IMAGE] &&
                            result[ENTRY_RESULT_RECORD_KEY.IMAGE_UPLOAD] === null
                        ) {
                            result[ENTRY_RESULT_RECORD_KEY.IMAGE_UPLOAD] = '';
                        }
                    });
                }

                // INFO：若沒有開啟 AI 功能，則視為已完成
                const isAIFlowFinished = AI_FLOW_KEY.every(
                    v => result[v] === null || Boolean(result[v])
                );

                /**
                 * INFO：若沒有開啟圖片上傳功能，則視為已完成
                 * INFO：目前僅支援一種上傳流程，所以只要有一個有值就代表上傳流程完成
                 */
                /**
                 * INFO：根據功能啟用狀態決定檢查哪個上傳流程
                 * - 如果開啟相框功能但沒有開 AI：檢查 CREATIVE_FRAME
                 * - 其他狀態：檢查 IMAGE_UPLOAD
                 */
                const isUploadFlowFinished = (() => {
                    const hasCreativeFrame = extraUploadFeatureTypes.includes(
                        EXTRA_UPLOAD_FEATURE_TYPES.CREATIVE_FRAME
                    );
                    // 如果開啟相框功能但沒有開 AI
                    if (hasCreativeFrame && !Boolean(enableAiImageExtensions)) {
                        return (
                            result[ENTRY_RESULT_RECORD_KEY.CREATIVE_FRAME] === null ||
                            Boolean(result[ENTRY_RESULT_RECORD_KEY.CREATIVE_FRAME])
                        );
                    }
                    // 其他狀態檢查 IMAGE_UPLOAD
                    return (
                        result[ENTRY_RESULT_RECORD_KEY.IMAGE_UPLOAD] === null ||
                        Boolean(result[ENTRY_RESULT_RECORD_KEY.IMAGE_UPLOAD])
                    );
                })();
                /**
                 * INFO：取得結果頁圖片上傳結果。
                 */
                if (extraUploadFeatureTypes.includes(EXTRA_UPLOAD_FEATURE_TYPES.IMAGE_UPLOAD)) {
                    if (Boolean(enableAiImageExtensions)) {
                        imageUrl = result.regenerativeImageExtension;
                    } else {
                        imageUrl = result.imageUpload;
                    }
                } else if (
                    extraUploadFeatureTypes.includes(EXTRA_UPLOAD_FEATURE_TYPES.CREATIVE_FRAME)
                ) {
                    imageUrl = result.creativeFrame;
                }

                return {
                    data: response,
                    ...result,
                    isAIFlowFinished,
                    isUploadFlowFinished,
                    imageUrl
                };
            } catch (error) {
                console.error('getEntryRecordResult error:', error);
                errHandler({
                    err: generateApiErrObj(error, clientErrTypes.GET_ENTRY_RECORD_RESULT_FAILED)
                });
            }
        }
        return result;
    }, [
        recordKey,
        enableTransitionPage,
        enableExtraUploadFeature,
        aiExtensions,
        promoUUID,
        currentEntryUUID,
        extraUploadFeatureTypes,
        errHandler
    ]);

    return { getEntryRecordResult };
}
